import { FC, useEffect, useState, useMemo } from 'react';
import { Stack, Button, Alert, Typography } from '@mui/material';
import {
  FormTextField,
  FormSelect,
  FormCheckbox,
  FieldsSection,
} from '../../../../../../../../shared/components/form';
import { AlternateEmail } from '@mui/icons-material';
import { Theme } from '../../../../../../../../shared/styles/theme/theme.ts';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  customerService,
  Bakery2bAccountsRes,
  ConnectBakery2bUserWithWawiCustomerRes,
} from '../../../../../../../customer/services/customer.service.ts';
import { ContentLoading } from '../../../../../../../../shared/components/loading/loading.component.tsx';
import {
  ExtendedSelectOption,
  bakery2bClientsState,
} from '../../../../../states/bakery2bClients.state.ts';
import { IFormData } from './newClient.popup.tsx';
import { useBeforeClosePopup } from '../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import { IPopupWrapper } from '../../../../../../../../shared/components/popup/components/popup.component.tsx';
import { take, tap, zipWith, filter } from 'rxjs';
import {
  configsData,
  DictBakery2bAccountsRes,
} from '../../../../../../../../shared/services/configsData/configsData.service.ts';
import { responseHandler } from '../../../../../../../../shared/responseHandler/responseHandler.ts';
import { resolveBakery2bConnectError } from '../../../../../../../customer/services/bakery2bConnectError.ts';
import { notificationCenter } from '../../../../../../../../shared/notificationCenter/notificationCenter.ts';
import { snackbarService } from '../../../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { storageHelper } from '../../../../../../../../shared/helpers/storage';

export const Content: FC<IContent> = ({ stream: popupStream }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [checkLoading, setCheckLoading] = useState(false);
  const [bakery2bAccountList, setBakery2bAccountList] = useState<DictBakery2bAccountsRes>([]);
  const [customerList, setCustomerList] = useState<Array<ExtendedSelectOption>>([]);
  const [isExistingAccount, setIsExistingAccount] = useState<boolean>(false);
  const [addedAccountToCustomerList, setAddedAccountToCustomer] = useState<Bakery2bAccountsRes>([]);

  const {
    watch,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    clearErrors,
  } = useFormContext<IFormData>();
  const isAddedAccountToCustomer = useMemo(() => {
    return !!addedAccountToCustomerList.find(
      (item) => item.email.toLowerCase() === (watch('email') || '').toLowerCase(),
    );
  }, [addedAccountToCustomerList, watch('email')]);
  useEffect(() => {
    const customerServiceSub = customerService.sub
      .listGridCustomers()
      .pipe(zipWith(configsData.sub.dictBakery2bAccounts()))
      .subscribe(([customerList, bakery2bAccount]) => {
        setBakery2bAccountList(bakery2bAccount);
        setCustomerList(customerList.map((el) => ({ ...el, label: el.internalOrFullName || '' })));
        setLoading(false);
      });

    customerService.pub.listGridCustomers({
      filter: {
        isActive: true,
      },
      cache: true,
    });
    configsData.pub.common(['dictBakery2bAccounts']);
    return () => {
      customerServiceSub.unsubscribe();
    };
  }, []);

  const filterOptions = (
    options: ExtendedSelectOption[],
    { inputValue }: { inputValue: string },
  ): ExtendedSelectOption[] =>
    options.filter(
      (option) =>
        (option.customerNo?.toLowerCase().includes(inputValue?.toLowerCase()) ||
          option.internalOrFullName?.toLowerCase().includes(inputValue?.toLowerCase())) as boolean,
    );

  const onSubmit = (v: IFormData) => {
    notificationCenter.notify(customerService.sub.connectBakery2bUserWithWawiCustomer(), {
      type: undefined,
      handler: (stream) => {
        stream
          .pipe(
            take(1),
            responseHandler({
              customErrorHandler: resolveBakery2bConnectError,
            }),
            tap(() => setLoading(false)),
            filter((res) => !!res),
          )
          .subscribe((res) => {
            storageHelper.memory.removeItem('configsData.dictBakery2bAccounts');
            reset();
            snackbarService.pub.show({
              type: 'success',
              content: t('settings.invitation_sent', {
                email: (res as ConnectBakery2bUserWithWawiCustomerRes).email,
              }),
            });
            popupStream.emit('close');
            bakery2bClientsState.pub.bakery2bClientList();
          });
      },
    });
    customerService.pub.connectBakery2bUserWithWawiCustomer({
      data: {
        name: v.name,
        email: v.email,
        customerId: String(v.customerId),
        note: v.note || undefined,
        orderAddNewItem: v.orderAddNewItem,
        orderNoDelivery: v.orderNoDelivery,
        orderShowPrice: v.orderShowPrice,
        standardOrderEdit: v.standardOrderEdit,
      },
    });
    setLoading(true);
  };

  useBeforeClosePopup({
    isDirty,
    stream: popupStream,
    handlers: () => {
      reset();
    },
  });

  return (
    <ContentLoading {...{ loading }}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack
          sx={{
            gap: 2,
            flexGrow: 1,
            width: '100%',
          }}
        >
          <FormTextField
            required
            name='email'
            width='100%'
            label={t('common.email')}
            slotProps={{
              input: { endAdornment: <AlternateEmail sx={{ color: Theme.palette.grey['600'] }} /> },
            }}
            onChange={(v) => {
              const existingAccount = bakery2bAccountList.find(
                (item) => item.email.toLowerCase() === v.toLowerCase(),
              );
              setIsExistingAccount(!!existingAccount);
              if (existingAccount) {
                setValue('name', existingAccount.name || '');
                clearErrors('name');
              }
            }}
            error={Boolean(errors['email'])}
            helperText={errors['email'] ? t(String(errors['email'].message)) : ''}
          />
          <FormTextField
            disabled={isExistingAccount}
            required
            name='name'
            width='100%'
            label={t('common.name')}
            error={Boolean(errors['name'])}
            helperText={errors['name'] ? t(String(errors['name'].message)) : ''}
          />
          <FormSelect
            loading={checkLoading}
            filterOptions={filterOptions}
            datasetattribute='customersOptions'
            required
            name='customerId'
            options={customerList as []}
            onChange={(_, opt) => {
              const typedOpt = opt as ExtendedSelectOption;
              if (typedOpt) {
                customerService.sub
                  .getCustomerBakery2bAccounts()
                  .pipe(
                    responseHandler<Bakery2bAccountsRes>({
                      errorReturnType: [],
                    }),
                    take(1),
                  )
                  .subscribe((res) => {
                    setCheckLoading(false);
                    setAddedAccountToCustomer(res);
                  });
                customerService.pub.getCustomerBakery2bAccounts({
                  id: String(typedOpt.id),
                });
                setCheckLoading(true);
              }
            }}
            width='100%'
            label={t('common.customer')}
            error={Boolean(errors['customerId'])}
            helperText={errors['customerId'] ? t(String(errors['customerId'].message)) : ''}
          />
          <Stack
            sx={{
              width: '100%',
            }}
          >
            <FormTextField
              name='note'
              label={t('common.note')}
              width='100%'
              multiline
              minRows={3}
              maxRows={5}
              slotProps={{ htmlInput: { maxLength: 128 } }}
            />
            <Typography
              sx={{
                textAlign: 'right',
                mt: '2px',
                mr: '2px',
                fontSize: 14,
                color: 'text.secondary',
              }}
            >
              {t('settings.left_symbols')}: {128 - (watch('note')?.length || 0)}
            </Typography>
          </Stack>
          <FieldsSection titleBoxSx={{ pt: 0 }} title={t('common.rights')}>
            <Stack
              sx={{
                paddingLeft: 1,
              }}
            >
              <FormCheckbox name='orderAddNewItem' label={t('settings.add_new_articles')} />
              <FormCheckbox
                name='orderNoDelivery'
                label={t('settings.order_no_delivery_on_this_day')}
              />
              <FormCheckbox name='orderShowPrice' label={t('settings.order_show_price_total')} />
              <FormCheckbox name='standardOrderEdit' label={t('settings.standard_order_edit')} />
            </Stack>
          </FieldsSection>
          {!isExistingAccount && !isAddedAccountToCustomer && watch('email') && (
            <Alert severity='info'>
              {t('customer.invitation_will_be_sent_to_email', {
                email: watch('email'),
              })}
            </Alert>
          )}
          {isExistingAccount && !isAddedAccountToCustomer && watch('email') && (
            <Alert severity='info'>
              {t('customer.account_for_email_already_registred_with_name', {
                email: watch('email'),
                name: watch('name'),
              })}
            </Alert>
          )}
          {isAddedAccountToCustomer && watch('email') && (
            <Alert severity='error'>
              {t('customer.an_account_has_already_been_added_for_this_customer')}
            </Alert>
          )}
        </Stack>
        <Stack
          direction='row'
          sx={{
            justifyContent: 'flex-end',
            mt: watch('email') ? 3 : 8,
          }}
        >
          <Button
            variant='contained'
            sx={{ minWidth: '125px' }}
            type='submit'
            disabled={checkLoading || isAddedAccountToCustomer}
          >
            {t('common.send')}
          </Button>
        </Stack>
      </form>
    </ContentLoading>
  );
};

interface IContent {
  stream: IPopupWrapper['stream'];
}
